// React i hooks
import { memo, useCallback, useEffect, useState } from "react";

// API servisi
import { getAnnualTaxAPI } from "services/api/calculations/annualtax/api";

// Tipovi i konstante
import { Errors } from "services/forms/validation/types";
import { AnnualTaxDetailProps } from "./types";
import { AnnualTaxDetailFormValues } from "services/api/calculations/annualtax/types";
import { initialAnnualTaxDetailForm } from "services/api/calculations/annualtax/consts";
import { CALCULATION_ANNUAL_TAX_EDIT_PAGE_TITLE } from "ui/components/PageTitle/consts";

// UI komponente
import { Loader } from "ui/components/Loader";
import { Button } from "ui/components/Button/Button";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";

// Lokalne komponente
import AnnualTaxEditCard from "../../../AnnualTaxEditCard/AnnualTaxEditCard";

// Helperi
import { useTranslate } from "helpers";


const AnnualTaxDetailCard: React.FC<
  AnnualTaxDetailProps
> = (props) => {
  const { annualTaxId } = props;
  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formValues, setFormValues] =
    useState<AnnualTaxDetailFormValues>(
      initialAnnualTaxDetailForm
    );

  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    if (annualTaxId) {
      fetchAnnualTax(annualTaxId);
    }
  }, [annualTaxId]);

  const fetchAnnualTax = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialAnnualTaxDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getAnnualTaxAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const handleEditSuccess = useCallback(() => {
    if (annualTaxId)
      fetchAnnualTax(annualTaxId);
    setShowEditModal(false);
  }, []);

  if (!annualTaxId) return <></>;

  return (
    <>
      <div className="  flex justify-center ">
        <div className="rounded-sm bg-white border max-w-lg w-full">
          <div className="divide-y divide-slate-200">
            <div className="flex justify-between items-center">
              <p className="text-sm font-semibold text-slate-600 px-4 py-3 uppercase">
                {t("admin.calculation.annualtax.detail.title")}
              </p>
              <div className="flex items-center gap-2 px-3 my-3 ">
                <Button
                  size="small"
                  variant="primary"
                  children={t("admin.change")}
                  onClick={() => setShowEditModal(true)}
                  startIcon="/media/icons/plus.svg"
                />
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.annualtax.detail.kwfrom")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.fromKw}
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.annualtax.detail.kwto")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.toKw} kw
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.annualtax.detail.price")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.price} $
              </div>
            </div>
          </div>
        </div>
      </div>
      {showEditModal && (
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <ModalHeader
            title={CALCULATION_ANNUAL_TAX_EDIT_PAGE_TITLE.text}
            onHide={() => setShowEditModal(false)}
          />
          <ModalBody>
            <AnnualTaxEditCard
              annualTaxId={annualTaxId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(AnnualTaxDetailCard);
